import type { CampaignSource, ICashbackLevel, IRebateCampaign, IUserProgress } from 'features/campaigns';
import { REBATE_CAMPAIGN_TYPE, REBATE_STATUS_ORDER } from 'features/campaigns';
import { evaluateCampaignStatus } from 'features/campaigns/hooks';
import { LEVEL_ORDER, formatNumberWithSpaces } from '../campaignHelpers';

export const REBATE_CARD_INFO_PLACEHOLDER = '---';

export interface RebateCardData {
  /** Display-ready claimed win amount, e.g. "10", or '---' when the rebate hasn't started. */
  claimedWinDisplay: string;
  /** Display-ready level progress, e.g. "Level 1 - 0%", or '---' when not shown. */
  progressDisplay: string;
  /** Whether the claimed-win value is a real amount (not '---') and should show currency. */
  showCurrency: boolean;
}

export const isRebateSource = (source: CampaignSource): source is IRebateCampaign => {
  return 'type' in source && source.type === REBATE_CAMPAIGN_TYPE;
};

/**
 * The campaign-wide gate is open when the player's spend has reached the
 * `minWinBetAmount` threshold.
 */
export const isGateUnlocked = (spent: number | null | undefined, minWinBetAmount: number): boolean =>
  !!spent && spent >= minWinBetAmount;

/**
 * A cashback level is unlocked when the campaign-wide gate is open and the
 * player's spend reached the level's floor.
 */
export const isLevelUnlocked = (spent: number | null | undefined, minWinBetAmount: number, level: ICashbackLevel): boolean =>
  !!spent && isGateUnlocked(spent, minWinBetAmount) && spent >= level.betRangeFrom;

/**
 * The cashback level the player's spend currently falls into (the highest level
 * whose `betRangeFrom` has been reached), or `undefined` if none apply.
 */
// fallow-ignore-next-line unused-export
export const findCurrentLevel = (levels: ICashbackLevel[], spent: number | null): ICashbackLevel | undefined =>
  spent === null
    ? undefined
    : [...levels].sort((a, b) => b.betRangeFrom - a.betRangeFrom).find((l) => spent >= l.betRangeFrom);

/**
 * Whether the player's spend currently falls within the given level's own
 * bet range (i.e. this is the level they're actively progressing through).
 */
export const isCurrentLevel = (spent: number | null | undefined, level: ICashbackLevel): boolean => {
  return !!spent && spent >= level.betRangeFrom && spent <= level.betRangeTo;
};

/**
 * A cashback level's fill percentage (0..100): 0 before the campaign-wide
 * gate opens, 100 once spend is past the level's range, the server-provided
 * `levelProgress` while it's the active level, and 0 otherwise.
 */
export const getLevelFillPercent = (
  userProgress: IUserProgress | null,
  level: ICashbackLevel,
  minWinBetAmount: number,
): number => {
  if (!userProgress) return 0; // user hasn't joined the campaign

  const spent = userProgress.spentBetAmount;
  const levelProgress = userProgress.levelProgress;
  const isCurrent = isCurrentLevel(spent, level);

  if (!isGateUnlocked(spent, minWinBetAmount)) return getUnlockedFillPercent(level, minWinBetAmount, levelProgress, isCurrent); // minWinBetAmount is not reached
  if (spent > level.betRangeTo) return 100; // Level fully behind the current spend — segment full.
  if (isCurrent) return levelProgress; // Active segment — filled by the server-computed level progress (already 0..100).
  return 0;
};

/**
 * Fill percentage for a level's segment while the campaign-wide gate
 * (`minWinBetAmount`) hasn't been reached yet by the player's spend.
 */
const getUnlockedFillPercent = (
  level: ICashbackLevel,
  minWinBetAmount: number,
  levelProgress: number,
  isCurrent: boolean,
): number => {
  if (level.levelNumber === 0) {
    if (isCurrent) return levelProgress; // Spend is still within level 0's own range — partial fill from the server.
    return 100; // Spend has already moved past level 0's own range, pre-gate — level 0 is fully complete.
  } else {
    if (minWinBetAmount >= level.betRangeFrom
      && minWinBetAmount <= level.betRangeTo && isCurrent) return levelProgress; // Gate threshold sits inside this level's range while it's the active one.
    else return 0; // Not the gated/active level — no fill yet.
  }
};

export const buildRebateCardInfo = (rebate: IRebateCampaign): RebateCardData => {
  const progress = rebate.userProgress;

  if (!progress) {
    return {
      claimedWinDisplay: REBATE_CARD_INFO_PLACEHOLDER,
      progressDisplay: REBATE_CARD_INFO_PLACEHOLDER,
      showCurrency: false,
    };
  }

  const started = !evaluateCampaignStatus(rebate).isPromo;
  const spentBetAmount = progress.spentBetAmount ?? 0;
  const minWinBetAmount = rebate.minWinBetAmount ?? 0;
  const levels = rebate.cashbackLevels ?? [];

  const claimedWinDisplay = started ? formatNumberWithSpaces(progress.claimedWin ?? 0) : REBATE_CARD_INFO_PLACEHOLDER;

  let progressDisplay = REBATE_CARD_INFO_PLACEHOLDER;
  if (started && isGateUnlocked(spentBetAmount, minWinBetAmount)) {
    const currentLevel = findCurrentLevel(levels, spentBetAmount);
    if (currentLevel) {
      progressDisplay = `Level ${currentLevel.levelNumber} - ${String(progress.levelProgress ?? 0)}%`;
    }
  }

  return { claimedWinDisplay, progressDisplay, showCurrency: started };
};

export const sortRebateCampaigns = (campaigns: IRebateCampaign[]): IRebateCampaign[] => {
  return [...campaigns].sort((a, b) => {
    // 1. Status: Active > Promo > Completed
    const statusDiff = REBATE_STATUS_ORDER[a.status] - REBATE_STATUS_ORDER[b.status];
    if (statusDiff !== 0) return statusDiff;

    // 2. Joined first
    if (a.joined !== b.joined) return a.joined ? -1 : 1;

    // 3. Priority (level): Gold > Purple > Red > Blue > Green > Madness
    const levelDiff = LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level];
    if (levelDiff !== 0) return levelDiff;

    // 4. End time ascending (closest end time first)
    return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
  });
};
